import { useMemo } from 'react';
import { Option } from '@/components/inputs/AutocompleteInput';
import { resolveSelectedOptionLabel } from '@/utils/selected-option-label';
import type { LogisticOperator } from '../../LogisticOperator/models';
import { useGetLogisticOperatorByIdQuery } from '../../LogisticOperator/services';
import { useGetCommerceByIdQuery } from '../../Commerce/services/commerces.service';

interface UseRoutesSearchBarSelectedOptionsProps {
  logisticOperatorId: string;
  commerceId: string;
}

const useRoutesSearchBarSelectedOptions = ({
  logisticOperatorId,
  commerceId,
}: UseRoutesSearchBarSelectedOptionsProps) => {
  const {
    data: logisticOperatorDetails,
    isFetching: isLogisticOperatorDetailsFetching,
  } = useGetLogisticOperatorByIdQuery(
    { logisticOperatorId },
    { skip: !logisticOperatorId }
  );

  const { data: commerceDetails, isFetching: isCommerceDetailsFetching } =
    useGetCommerceByIdQuery({ commerceId }, { skip: !commerceId });

  const selectedLogisticOperator = useMemo((): Option | null => {
    if (!logisticOperatorId) return null;
    if (logisticOperatorDetails) {
      return {
        label: logisticOperatorDetails.fullName,
        data: logisticOperatorDetails,
      };
    }
    return {
      label: resolveSelectedOptionLabel(null, {
        isResolving: isLogisticOperatorDetailsFetching,
        fallback: 'Operador seleccionado',
      }),
      data: {
        id: logisticOperatorId,
        fullName: '',
        businessName: '',
      } as LogisticOperator,
    };
  }, [
    logisticOperatorId,
    logisticOperatorDetails,
    isLogisticOperatorDetailsFetching,
  ]);

  const selectedCommerce = useMemo((): Option | null => {
    if (!commerceId) return null;
    return {
      label: resolveSelectedOptionLabel(commerceDetails?.name, {
        isResolving: isCommerceDetailsFetching,
        fallback: 'Comercio seleccionado',
      }),
      data: commerceId,
    };
  }, [commerceId, commerceDetails?.name, isCommerceDetailsFetching]);

  return {
    selectedLogisticOperator,
    selectedCommerce,
  };
};

export default useRoutesSearchBarSelectedOptions;
